import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, FileText } from "lucide-react";
import { PageHeader } from "../components/lendx/PageHeader";
import { Reveal, SectionHeading } from "../components/lendx/Reveal";
import { CTABanner } from "../components/lendx/CTABanner";
import { EnquiryForm } from "../components/lendx/EnquiryForm";
import { ALL_SERVICES } from "../lib/data";
import { applySEO, setJsonLd } from "../lib/scroll";

const CheckList = ({ items, testId }) => (
  <ul className="space-y-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-gray-600 md:text-base" data-testid={`${testId}-item-${i + 1}`}>
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-gold-line bg-gold-tint">
          <Check size={11} className="text-gold-deep" />
        </span>
        {item}
      </li>
    ))}
  </ul>
);

export default function ServicePage() {
  const { slug } = useParams();
  const service = ALL_SERVICES.find((s) => s.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (service) {
      applySEO(
        `${service.title} | LendX Loan Advisory India`,
        `${service.short} Explore suitable ${service.title.toLowerCase()} options with expert advisory and end-to-end coordination from LendX.`
      );
      window.dispatchEvent(new CustomEvent("lendx:requirement", { detail: service.title }));
      const base = "https://www.lendxpro.com";
      setJsonLd("ld-service", {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.title,
        description: service.intro,
        provider: { "@type": "FinancialService", name: "LendX", url: base },
        areaServed: { "@type": "Country", name: "India" },
        url: `${base}/solutions/${service.slug}`,
      });
      setJsonLd("ld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
      setJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: base },
          { "@type": "ListItem", position: 2, name: "Loan Solutions", item: `${base}/solutions` },
          { "@type": "ListItem", position: 3, name: service.title, item: `${base}/solutions/${service.slug}` },
        ],
      });
    }
    return () => {
      setJsonLd("ld-service", null);
      setJsonLd("ld-faq", null);
      setJsonLd("ld-breadcrumb", null);
    };
  }, [service]);

  if (!service) return <Navigate to="/solutions" replace />;
  const Icon = service.icon;

  return (
    <main data-testid={`service-page-${service.slug}`}>
      <PageHeader overline={service.category === "advisory" ? "Premium & Customised Services" : "Loan Solutions"} title={service.headline} sub={service.intro} testId={`service-${service.slug}`} />

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-4 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-xl border border-gray-200 bg-white p-8 shadow-[0_2px_10px_rgba(16,20,24,0.04)] md:p-10" data-testid="suitable-for-card">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold-line bg-gold-tint text-gold-deep">
                  <Icon size={20} strokeWidth={1.6} />
                </span>
                <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-navy">Who Is This Suitable For?</h2>
                <div className="mt-6">
                  <CheckList items={service.suitableFor} testId="suitable" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-xl border border-gray-200 bg-white p-8 shadow-[0_2px_10px_rgba(16,20,24,0.04)] md:p-10" data-testid="benefits-card">
                <h2 className="font-heading text-2xl font-bold tracking-tight text-navy">Key Benefits</h2>
                <div className="mt-6">
                  <CheckList items={service.benefits} testId="benefit" />
                </div>
              </div>
            </Reveal>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-xl border border-gray-200 bg-white p-8 shadow-[0_2px_10px_rgba(16,20,24,0.04)] md:p-10" data-testid="options-card">
                <h2 className="font-heading text-2xl font-bold tracking-tight text-navy">Financing Options Available</h2>
                <div className="mt-6 flex flex-wrap gap-3">
                  {service.options.map((o) => (
                    <span key={o} className="rounded-full border border-gray-200 bg-fog px-4 py-2 text-xs font-medium text-gray-600 transition-colors duration-300 hover:border-orange hover:text-orange-deep">
                      {o}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-xl border border-gray-200 bg-white p-8 shadow-[0_2px_10px_rgba(16,20,24,0.04)] md:p-10" data-testid="documents-card">
                <h2 className="flex items-center gap-3 font-heading text-2xl font-bold tracking-tight text-navy">
                  <FileText size={20} className="text-gold-deep" /> Typical Documents Required
                </h2>
                <ol className="mt-6 space-y-3">
                  {service.documents.map((d, i) => (
                    <li key={i} className="flex items-baseline gap-4 border-b border-gray-100 pb-3 text-sm text-gray-600">
                      <span className="font-mono text-xs text-gold-deep">{String(i + 1).padStart(2, "0")}</span>
                      {d}
                    </li>
                  ))}
                </ol>
                <p className="mt-5 text-xs leading-relaxed text-gray-400">
                  Actual documentation varies by financial institution and applicant profile.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-20">
            <SectionHeading overline="FAQs" title="Frequently Asked Questions" testId="service-faq" />
            <div className="mt-10 max-w-3xl divide-y divide-gray-200 border-y border-gray-200">
              {service.faqs.map((f, i) => (
                <div key={i}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    data-testid={`faq-toggle-${i + 1}`}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span className="font-heading text-lg font-semibold text-navy">{f.q}</span>
                    <ChevronDown size={18} className={`shrink-0 text-gold-deep transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 text-sm leading-relaxed text-gray-600 md:text-base" data-testid={`faq-answer-${i + 1}`}>{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABanner title={`Discuss Your ${service.title} Requirement`} />
      <EnquiryForm />
    </main>
  );
}
